import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import horizontalScrollCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/HorizontalScrollCacheStateManager'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'
import classnames from 'classnames'
import React, { Children, cloneElement, ReactNode } from 'react'

import useActiveTab from './hooks/useActiveTab'
import styles from './tabs.module.scss'
export interface TabProps {
  children: ReactNode
  id: string
  title: string | ReactNode
  active?: boolean
  headerClassName?: string
  panelClassName?: string
  tracker?: string
  trackingMetaData?: TrackingMetaData | undefined
}

export const Tab = ({ children, active, panelClassName }: TabProps) => {
  return (
    <div
      className={classnames(
        styles.tabs__tab_panel,
        active ? styles.tabs__active_tab_panel : styles.tabs__inactive_tab_panel,
        panelClassName,
      )}
    >
      {children}
    </div>
  )
}

export interface TabsProps {
  children?: React.ReactElement<TabProps> | (React.ReactElement<TabProps> | null)[]
  activeTabId?: string
  onChange?: (activeId: string) => void
  className?: string
  tracker?: string
  tabButtonWrapperClassname?: string
  activeTabHeaderClassName?: string
}

const Tabs = ({
  activeTabId: controlledActiveTabId,
  onChange,
  children,
  className,
  tracker,
  tabButtonWrapperClassname,
  activeTabHeaderClassName,
}: TabsProps) => {
  const classes = classnames(styles.tabs, className)
  const firstTabId = (Children.toArray(children)[0] as React.ReactElement<TabProps>).props.id

  const savedActiveTabId = tracker ? horizontalScrollCacheStateManager.get<string>(tracker) : undefined

  const { handleChange, activeTabId } = useActiveTab({
    initialActiveTabId: savedActiveTabId ?? firstTabId,
    controlledActiveTabId,
    onChange,
  })

  const handleTabClick = (id: string, tabTitle: string | ReactNode, trackingMetaData?: TrackingMetaData) => {
    handleChange(id)
    if (tracker) {
      trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        trackGA: true,
        trackInternal: true,
        // @i18n-ignore
        category: 'navigation',
        action: 'click__tabs__tab',
        label: tabTitle,
        value: tracker,
        context: trackingMetaData?.context,
      })
      horizontalScrollCacheStateManager.set(tracker, id)
    }
  }

  return (
    <div className={classes}>
      <div className={classnames(styles.tabs__button_wrap, 'tabButtonWrap', tabButtonWrapperClassname)}>
        {Children.map(children, tab => {
          if (!tab) return null
          return (
            <button
              key={tab.props.id}
              onClick={() => {
                handleTabClick(tab.props.id, tab.props.title, tab.props.trackingMetaData)
              }}
              className={`${classnames(styles.tabs__button, [
                activeTabId === tab.props.id ? styles.tabs__active_tab : styles.tabs__inactive_tab,
                tab.props.headerClassName,
                activeTabId === tab.props.id && activeTabHeaderClassName,
              ])} ${activeTabId === tab.props.id ? 'activeTab' : 'inactiveTab'}`}
            >
              {tab.props.title}
            </button>
          )
        })}
      </div>
      {Children.map(children, tab => {
        if (!tab) return null

        if (activeTabId !== tab.props.id) {
          return null
        }

        return cloneElement(tab, {
          ...tab.props,
          active: activeTabId === tab.props.id,
        })
      })}
    </div>
  )
}

export interface TabPanel {
  tabId: string
  panel: ReactNode
}

export default Tabs
